import { fetchFromRTM } from './index';
import * as constants from './constants';
import './typedef';

class AssetResolver {
  constructor(connection) {
    this.connection = connection;
  }

  /**
   * Fetches an asset by URN. See
   *   [Rtm Docs](https://dev-developers.smartsparrow.com/messages/author-asset-get-ok/)
   * @param {string} urn
   * @return {Promise.<AssetDTO>}
   */
  async authorAssetGet(urn) {
    const message = {
      type: constants.RTM_AUTHOR_ASSET_GET,
      urn,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ASSET_GET_OK,
      constants.RTM_AUTHOR_ASSET_GET_ERROR
    );
    return response.asset;
  }

  /**
   * Adds an asset to a courseware element. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-courseware-asset-add/)
   * @param {string} elementId
   * @param {string} elementType
   * @param {string} assetURN
   * @returns {Promise.<void>}
   */
  async authorCoursewareAssetAdd(elementId, elementType, assetURN) {
    const message = {
      type: constants.RTM_AUTHOR_COURSEWARE_ASSET_ADD,
      elementId,
      elementType,
      assetURN,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_COURSEWARE_ASSET_ADD_OK,
      constants.RTM_AUTHOR_COURSEWARE_ASSET_ADD_ERROR
    );
    return response;
  }

  /**
   * Remove asset from courseware element. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-courseware-asset-remove/)
   * @param {string} elementId
   * @param {string} elementType
   * @param {string} assetURN
   * @returns {Promise.<void>}
   */
  async authorCoursewareAssetRemove(elementId, elementType, assetURN) {
    const message = {
      type: constants.RTM_AUTHOR_COURSEWARE_ASSET_REMOVE,
      elementId,
      elementType,
      assetURN,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_COURSEWARE_ASSET_REMOVE_OK,
      constants.RTM_AUTHOR_COURSEWARE_ASSET_REMOVE_ERROR
    );
    return response;
  }

  /**
   * Create an asset from an external source. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-asset-create/)
   * @param {string} url
   * @param {string} mediaType
   * @param {string} assetVisibility
   * @param {string} assetProvider
   * @param {Map<string, string>} metadata
   * @returns {Promise.<AssetDTO>}
   */
  async authorAssetCreate(url, mediaType, metadata) {
    const message = {
      type: constants.RTM_AUTHOR_ASSET_CREATE,
      url,
      mediaType,
      assetVisibility: 'GLOBAL',
      assetProvider: 'EXTERNAL',
    };
    if (metadata) {
      message.metadata = metadata;
    }

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ASSET_CREATE_OK,
      constants.RTM_AUTHOR_ASSET_CREATE_ERROR
    );
    return response.asset;
  }

  /**
   * Fetch a list of icon assets by library.
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-icon-asset-list/)
   * @param {List} iconLibraries
   * @returns {Promise.<[IconAssetSummaryDto]>}
   */
  authorIconAssetList = async ({ iconLibraries }) => {
    const message = {
      type: constants.RTM_AUTHOR_ICON_ASSET_LIST,
      iconLibraries,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ICON_ASSET_LIST_OK,
      constants.RTM_AUTHOR_ICON_ASSET_LIST_ERROR,
    );
    return response.iconAssetSummaries;
  }

  /**
   * Send message to create alfreso asset in Bronte
   *
   * @param {string} payload.alfrescoNodeId
   * @returns {Promise.<void>} An empty promise
   */
  // eslint-disable-next-line no-unused-vars
  authorAssetAlfrescoSelect = async (alfrescoNodeId, hasChild) => {
    const message = {
      type: constants.RTM_AUTHOR_ASSET_ALFRESCO_SELECT,
      alfrescoNodeId,
      ...(hasChild && { hasChild }),
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ASSET_ALFRESCO_SELECT_OK,
      constants.RTM_AUTHOR_ASSET_ALFRESCO_SELECT_ERROR,
    );
    return response;
  }

  /**
   * Send message to get count of assets to be pushed
   *
   * @param {string} payload.activityId
   * @returns {Promise.<void>} An empty promise
   */
  authorAlfrescoAssetsPushCount = async (activityId) => {
    const message = {
      type: constants.RTM_AUTHOR_ALFRESCO_ASSETS_PUSH_COUNT,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ALFRESCO_ASSETS_PUSH_COUNT_OK,
      constants.RTM_AUTHOR_ALFRESCO_ASSETS_PUSH_COUNT_ERROR,
    );
    return response;
  }

  /**
   * Send message to publish images from activity to alfresco
   *
   * @param {string} payload.activityId
   * @param {string} payload.alfrescoNodeId
   * @returns {Promise.<void>} An empty promise
   */
  authorAlfrescoAssetsPush = async (activityId, alfrescoNodeId) => {
    const message = {
      type: constants.RTM_AUTHOR_ALFRESCO_ASSETS_PUSH,
      activityId,
      alfrescoNodeId,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ALFRESCO_ASSETS_PUSH_OK,
      constants.RTM_AUTHOR_ALFRESCO_ASSETS_PUSH_ERROR,
    );
    return response;
  }

  /**
   * Send message to get start sync of bronte assets to alfreso
   *
   * @param {string} payload.activityId (courseId)
   * @param {string} payload.syncType (PUSH|PULL)
   * @returns {Promise.<void>} (courseId, syncType, inProgres (boolean))
   */
  authorAlfrescoAssetsProgress = async (activityId, syncType) => {
    const message = {
      type: constants.RTM_AUTHOR_ALFRESCO_ASSETS_PROGRESS,
      activityId,
      syncType,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ALFRESCO_ASSETS_PROGRESS_OK,
      constants.RTM_AUTHOR_ALFRESCO_ASSETS_PROGRESS_ERROR,
    );
    return response;
  }
}

export default AssetResolver;
